import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  AddButton,
  BackButton,
  ThinDropdown,
  ThinTextField,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Loader from "../../../components/partials/Loader";

const EditDistricts = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loader, setLoader] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [district, setDistrict] = useState({
    District_Name: "",
    Status: 1,
  });

  const validationSchema = Yup.object().shape({
    District_Name: Yup.string()
      .min(3, "District name must be at least 3 characters long")
      .matches(
        /^[a-zA-Z\s]+$/,
        "District name can only contain letters and spaces"
      )
      .required("District name is required"),
    Status: Yup.string().required("Status is required"),
  });

  // Fetch district data
  useEffect(() => {
    const fetchDistrictData = async () => {
      try {
        const response = await fetchData(
          `all-districts`,
          "GET",
          null,
          {},
          { District_Id: id },
          navigate,
          "/districts",
          dispatch
        );

        const data = response?.data?.[0];
        if (data) {
          setDistrict({
            District_Name: data?.District_Name || data?.district_name,
            Status: data?.Status ?? data?.status ?? 0,
          });
          setLoader(false);
        }
      } catch (error) {
        setLoader(false);
        console.error("Error fetching district data:", error);
        toast.error("Failed to load district data");
      }
    };

    fetchDistrictData();
  }, [id, navigate, dispatch]);

  const formik = useFormik({
    initialValues: district,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        setIsSubmitting(true);

        const data = {
          District_Id: id,
          District_Name: values.District_Name,
          Status: values.Status,
        };

        const response = await fetchData(
          `districts`,
          "PUT",
          data,
          {},
          {},
          navigate,
          "/districts",
          dispatch
        );
        toast.success(response?.message || "District updated successfully");
        navigate("/districts");
        resetForm();
      } catch (error) {
        console.error("Error updating district:", error);
        toast.error(error?.message || "Failed to update district");
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="w-full my-4 mx-auto">
      {loader && <Loader />}

      <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
        <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
          Edit District
        </h1>
        <BackButton text="Back" to="/districts" />
      </div>

      <div className="bg-white rounded-lg shadow w-full lg:mb-[30px] mt-4 mb-[7rem]">
        <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b ">
          <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
            Edit District
          </h1>
        </div>
        <form className="p-4" onSubmit={formik.handleSubmit}>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <ThinTextField
              id="District_Name"
              label="District"
              placeholder="District"
              type="text"
              name="District_Name"
              value={formik.values.District_Name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched.District_Name &&
                Boolean(formik.errors.District_Name)
              }
              helperText={
                formik.touched.District_Name && formik.errors.District_Name
              }
            />

            <ThinDropdown
              label="Status"
              id="status-dropdown"
              name="Status"
              options={[
                { value: 1, label: "Active" },
                { value: 0, label: "In-Active" },
              ]}
              value={formik.values.Status}
              onChange={(e) => formik.setFieldValue("Status", e.target.value)}
              onBlur={formik.handleBlur}
              error={formik.touched.Status && Boolean(formik.errors.Status)}
              helperText={formik.touched.Status && formik.errors.Status}
            />
          </div>

          <div className="p-4">
            <AddButton
              text="Submit"
              type="Submit"
              isLoading={isSubmitting}
              disabled={formik.isSubmitting}
            />
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDistricts;
